import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useLocation, Navigate } from "react-router-dom";
import { switchLangPath } from "../../lib/i18n";
import { SiteProvider, useSiteQuery } from "../../lib/site";
import { img, BACKEND, api } from "../../lib/api";
import Header from "../../components/site/Header";
import Footer from "../../components/site/Footer";
import { SiteLoader, MaintenanceScreen } from "../../components/site/Common";
import PromoModal from "./PromoModal";

function Promo() {
  const { search } = useLocation();
  // Suppress promo modal when inside admin live preview (iframe loads with ?preview=1)
  const isPreview = new URLSearchParams(search).get("preview") === "1";
  const { data } = useQuery({
    queryKey: ["public", "promo"],
    queryFn: () => api.get("/public/promo").then((r) => r.data),
    staleTime: 60_000,
    enabled: !isPreview,
  });
  if (isPreview || !data?.enabled) return null;
  return <PromoModal promo={data} />;
}

function Analytics({ seo }) {
  useEffect(() => {
    const id = seo?.ga_id;
    if (!id || !/^G-[A-Z0-9]{4,12}$/.test(id) || window.gtag) return;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", id);
  }, [seo?.ga_id]);
  return seo?.gsc_code ? <meta name="google-site-verification" content={seo.gsc_code} /> : null;
}

function Favicon({ href }) {
  useEffect(() => {
    if (!href) return;
    let el = document.querySelector("link[rel='icon']");
    if (!el) { el = document.createElement("link"); el.rel = "icon"; document.head.appendChild(el); }
    el.href = img(href);
  }, [href]);
  return null;
}

function VisitTracker() {
  const { pathname, search } = useLocation();
  const lastRef = useRef({ path: "", t: 0 });
  useEffect(() => {
    // Suppress visit tracking inside admin live preview iframe.
    if (new URLSearchParams(search).get("preview") === "1") return;
    // Only track on route change; dedupe fast consecutive calls (StrictMode double-run).
    const now = Date.now();
    if (lastRef.current.path === pathname && now - lastRef.current.t < 1500) return;
    const prevPath = lastRef.current.path;
    const prevT = lastRef.current.t;
    lastRef.current = { path: pathname, t: now };
    // Admin / draft paths never tracked. Short delay so SPA UI isn't blocked.
    if (pathname.startsWith("/admin")) return;
    const dur = prevT ? Math.min(now - prevT, 1800000) : 0;
    const body = JSON.stringify({ path: pathname, ref: prevPath ? "" : document.referrer || "", duration_ms: dur });
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${BACKEND}/api/public/_track`, new Blob([body], { type: "application/json" }));
      } else {
        fetch(`${BACKEND}/api/public/_track`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
      }
    } catch { /* ignore */ }
  }, [pathname, search]);
  return null;
}

const inScope = (scope, mode) => !scope || scope === "both" || scope === mode;

function forTheme(s) {
  const mode = s.theme?.mode === "adaylik" ? "adaylik" : "normal";
  const p = s.profile || {};
  const socials = { ...(p.socials || {}) };
  if (mode === "adaylik") for (const k in p.socials_split || {}) if (p.socials_split[k]) socials[k] = (p.socials_campaign || {})[k] || "";
  const menu = s.menu.filter((m) => inScope(m.scope, mode)).map((m) => ({ ...m, children: (m.children || []).filter((c) => inScope(c.scope, mode)) }));
  const socials_links = (p.socials_custom || []).map((x) => ({ label: x.label, icon: x.icon, url: mode === "adaylik" && x.split ? x.url_campaign : x.url }));
  return { ...s, menu, profile: { ...p, socials, socials_links } };
}

export { inScope };

export default function PublicLayout({ lang }) {
  const { data: rawSite, isLoading } = useSiteQuery();
  const site = rawSite && forTheme(rawSite);
  const { pathname, hash } = useLocation();
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  useEffect(() => {
    if (hash) setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 300);
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  if (isLoading || !site)
    return <SiteLoader />;
  const mg = rawSite.general || {};
  if (mg.maintenance && !localStorage.getItem("maint_bypass")) {
    const mt = mg.maintenance_title?.[lang] || mg.maintenance_title?.tr;
    const mx = mg.maintenance_text?.[lang] || mg.maintenance_text?.tr;
    return <MaintenanceScreen title={mt} text={mx} name={rawSite.general?.site_name?.tr || rawSite.profile?.name || "Olgu Tansuğ"} />;
  }
  if (lang !== "tr" && !(site.language?.active || ["tr"]).includes(lang)) return <Navigate to={switchLangPath(pathname, lang, "tr")} replace />;
  const mode = site.theme?.mode === "adaylik" ? "adaylik" : "normal";
  return (
    <SiteProvider lang={lang} site={site}>
      <div className="site-root grain font-body" data-theme={mode} style={{ "--campaign": site.theme?.accent || "#DC2626" }} data-testid={`site-theme-${mode}`}>
        <Analytics seo={site.seo} />
        <Favicon href={site.general?.favicon} />
        <VisitTracker />
        <Header />
        <main className="relative z-[2]"><Outlet /></main>
        <Footer />
        <Promo />
      </div>
    </SiteProvider>
  );
}
